import { createContext } from "react";
import { SceneExtended, SceneSaveResult } from "./types";
import { ScenePatch } from "./useSceneDocStore";
import type { StreamingStatus } from "components/PStreaming/streamingSession";

// Main Scenes List Context Interface
export interface ScenesContextModel {
	scenes: SceneExtended[];
	current?: SceneExtended;
	loading: boolean;
	fetchScenes: () => void;
	setCurrent?: React.Dispatch<React.SetStateAction<SceneExtended | undefined>>;
	/**
	 * Deep-merges a patch into the open scene. Prefer this over setCurrent for
	 * updating individual fields — it merges against the live document, so a
	 * caller holding an outdated copy cannot revert fields it never touched.
	 */
	patchCurrent?: (patch: ScenePatch) => void;
	currentLoading?: boolean;
	updateScene?: () => any;
	setCurrentScene?: (scene: any) => void;
	setRefreshTopics?: (refresh: boolean) => void;
	streamingCloseEvent?: any;
	setStreamingCloseEvent?: (event: any) => void;
	// Additional properties that may be used
	query?: any;
	pagedResponse?: any;
	setQuery?: (query: any) => void;
	loadMore?: () => void;
	saveLocationScene?: (data: any) => void;
	saveEditorScene?: (data: any) => Promise<SceneSaveResult>;
	/**
	 * Copies the open scene into a new one under `name`, optionally replacing the
	 * copy's content. Leaves the open scene untouched; the new scene is only
	 * visible after a fetchScenes().
	 */
	saveSceneAs?: (name: string, sceneContent?: unknown) => Promise<boolean>;
	saveDetailScene?: (data: any) => void;
	savePartialSceneInformation?: (data: any) => void;
	objectIdsInScene?: any[];
	objectIdsInWishlists?: any[];
	setObjectIdsInWishlists?: (ids: any[]) => void;
	createShareLink?: any;
	currentBimcollab?: any;
	setCurrentBimcollab?: (data: any) => void;
	BIMIssues?: any[];
	setBIMIssues?: (issues: any[]) => void;
	refreshTopics?: boolean;
	lodValue?: any;
	setLodValue?: (value: any) => void;
	availableLods?: any[];
	// Additional properties from SceneView and other components
	sendTrackingMessage?: (eventType: string, message?: any) => void;
	// Additional streaming/arcware properties
	closeStreaming?: () => void;
	streamingState?: any;
	setStreamingState?: (state: any) => void;
	isStreamingCleaningUp?: boolean;
	/** What the cloud reports while a streaming session starts: instance booting, queue. */
	streamingStatus?: StreamingStatus;
	applicationResponse?: any;
	setApplicationResponse?: (response: any) => void;
	sendMessage?: (message: any) => void;
	/**
	 * Ask a running streaming session to read the current scene again. The load
	 * command otherwise only goes out when the scene KEY changes, so a session
	 * shows the scene as it was when it started — see the provider.
	 */
	reloadStreamingScene?: () => void;
	arcwareApplication?: any;
	sceneLoaded?: boolean;
	setSceneLoaded?: (loaded: boolean) => void;
	orgProjScene?: any;
	setOrgProjScene?: (scene: any) => void;
	streamingResponse?: any;
	setStreamingResponse?: (response: any) => void;
	setArcwareApplication?: (app: any) => void;
	pixelStreaming?: any;
	setPixelStreaming?: (streaming: any) => void;
	setFlag?: (flag: any) => void;
	// Viewpoints properties
	viewpoints?: any[];
	setViewpoints?: (viewpoints: any[]) => void;
	fetchViewpoints?: () => void;
	// Editor state properties
	editorState?: any;
	setEditorState?: (state: any) => void;
	// Editor mode: 'edit' for the 3D editor, 'plan' for the floorplanner overlay
	editorMode?: 'edit' | 'plan';
	// Setting 'plan' is a no-op when isPlanModeAvailable is false.
	setEditorMode?: (mode: 'edit' | 'plan') => void;
	// Whether the current scene has a linked floorplan and can enter plan mode
	// (see contexts/Scenes/sceneCapabilities.ts for what this does and doesn't gate)
	isPlanModeAvailable?: boolean;
	// Floorplan document linked to the current scene via scene.plan.floorplanId
	linkedFloorplan?: { floors: Array<{ name: string; data: string; rooms?: any[] }> } | null;
}

const Context = createContext<ScenesContextModel>({
	scenes: [],
	loading: false,
	fetchScenes: () => {
		throw new Error("fetchScenes not implemented");
	},
});

export default Context;
